/**
 * Resolves a card's `lastEditedByUserId` to a display name (SE-6866).
 *
 * The pipeline card footer reads `Updated 2d ago · Hugo`, but `ListKanbanCards`
 * carries only the raw editor id: `User` exposes no name scalars (names live
 * under `cognitoUser.Attributes`), so selecting the name through
 * `KanbanCard.lastEditor` would mean two extra levels of object nesting under
 * every edge of a 250-card read — which the SE-5063 complexity budget, at
 * 1787/2000 before this ticket, cannot afford.
 *
 * So the id is resolved client-side against the ONE org-roster query the board
 * sharing sheet, the audit trail and note auto-share already run. The
 * derivation is `buildActorNameMap` (SE-5504) — the same map the trail names
 * its actors through, so a person is spelled identically on the card and in the
 * log. Every editor of a card is by definition a member of the viewer's
 * organization, so a single roster read resolves every name on the board.
 *
 * **Called per card, deliberately.** Every mounted card runs this hook with the
 * same variables, so Apollo collapses them into ONE network request and every
 * subsequent card reads the normalized cache entry (the precedent is
 * `useShareModal`). Hoisting it to the board container would mean threading a
 * resolver through the column renderer for no fetch saved.
 * @module features/player-kanban/hooks/useCardEditorName
 */

import { useMemo } from "react";

import { useListKanbanOrganizationUsersQuery } from "@/generated/graphql";

import { ORG_USERS_PAGE_SIZE } from "../constants";
import { buildActorNameMap } from "../utils/pipelineActorNames";

/**
 * Resolve the last editor's display name for one card.
 * @remarks
 * The `skip` guard is on DATA READINESS, not on anything about the card's
 * visibility: with no id there is no name to resolve, so a board of never-edited
 * cards issues no roster read at all. Once any card carries an id the read
 * happens once and the rest resolve from the cache.
 *
 * `null` is returned for an id the roster cannot place — a departed member, or
 * simply the window before the roster response lands. Both render the time-only
 * footer rather than a placeholder or a raw UUID, which is
 * `formatCardUpdatedFooter`'s specified degradation.
 * @param userId - The card's `lastEditedByUserId`.
 * @returns The editor's display name, or `null` when unknown.
 */
export const useCardEditorName = (
  userId: string | null | undefined
): string | null => {
  const { data } = useListKanbanOrganizationUsersQuery({
    variables: { first: ORG_USERS_PAGE_SIZE },
    skip: !userId,
  });

  const actorNames = useMemo(
    () =>
      buildActorNameMap(
        (data?.listMyOrganizationsOrganizationUsers?.edges ?? []).map(
          edge => edge.node
        )
      ),
    [data]
  );

  return userId ? (actorNames.get(userId) ?? null) : null;
};
